/**
 * Photo Gallery — a sliding strip of captioned photographs.
 *
 * Three changes from the prototype's script:
 *
 *  - It binds to every `.photo-gallery` on the page rather than the ids
 *    `tourTrack` / `tourPrev` / `tourNext`, so two of these blocks on one
 *    page do not drive each other.
 *  - It survives a gallery smaller than one viewful. The prototype always
 *    renders six photographs; the block has to hold three, where the track
 *    never overflows on a wide screen and both arrows stay disabled.
 *  - The resize handler is debounced. `apply()` reads
 *    `getBoundingClientRect()` and `scrollWidth`, so running it on every
 *    resize event lays out the page dozens of times a drag.
 *
 * Unlike Post Carousel, off-screen slides are NOT marked `inert`. There is
 * nothing focusable inside one — a figure, an image and a caption — so no
 * keyboard user can fall into the hidden part of the track. Hiding them
 * would only take the captions away from a screen reader, which can
 * otherwise read the whole gallery without operating the controls.
 *
 * `perView` has to agree with the breakpoints in
 * src/sass/blocks/_photo-gallery.scss or the track lands mid-photograph.
 *
 * No optional chaining or nullish coalescing: gulp-babel has no preset
 * configured, so uglify-js parses this as written.
 */
(function () {
  const blocks = document.querySelectorAll(".photo-gallery");
  if (!blocks.length) return;
  function setup(root) {
    const track = root.querySelector(".gal-track");
    const view = root.querySelector(".gal-view");
    const prev = root.querySelector(".gal-prev");
    const next = root.querySelector(".gal-next");
    const fill = root.querySelector(".gal-fill");
    const now = root.querySelector(".gal-now");
    if (!track || !view || !prev || !next) return;
    const slides = track.children;
    if (!slides.length) return;
    let index = 0;
    function perView() {
      const w = window.innerWidth;
      if (w <= 680) return 1;
      if (w <= 1000) return 1.6;
      return 2.35;
    }
    function maxIndex() {
      return Math.max(0, Math.ceil(slides.length - perView()));
    }

    // The furthest the track can travel before its last photograph is flush
    // with the right edge. With a fractional perView the index alone
    // overshoots on the final step, so the shift is clamped to this.
    function maxShift() {
      return Math.max(0, track.scrollWidth - view.clientWidth);
    }
    function apply() {
      const pv = perView();
      const max = maxIndex();
      if (index > max) index = max;
      const first = slides[0];
      if (!first) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 4;
      const step = first.getBoundingClientRect().width + gap;
      const limit = maxShift();
      const shift = Math.min(index * step, limit);
      track.style.transform = "translate3d(" + -shift + "px,0,0)";
      prev.disabled = index === 0;
      // Compared in pixels rather than against maxIndex, because a
      // fractional perView can leave the last step short of a whole slide.
      next.disabled = shift >= limit - 1;
      if (now) now.textContent = index + 1;
      if (fill) {
        const pct = Math.min(100, pv / slides.length * 100);
        fill.style.width = pct + "%";
        const travel = max === 0 ? 0 : index / max * (100 - pct);
        fill.style.transform = "translateX(" + travel / (pct / 100) + "%)";
      }
    }
    prev.addEventListener("click", function () {
      index = Math.max(0, index - 1);
      apply();
    });
    next.addEventListener("click", function () {
      index = Math.min(maxIndex(), index + 1);
      apply();
    });
    let resizeTimer = null;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(apply, 120);
    });
    apply();
  }
  for (let i = 0; i < blocks.length; i++) {
    setup(blocks[i]);
  }
})();